import * as React from "react"
import { AlertTriangle, Calendar, Landmark, Tag, Trash2, Loader2 } from "lucide-react"

import type { Account, Category } from "@/types"
import type { CollectionTransaction } from "../../types"
import { collectionLabels } from "../../utils/constants"
import { formatTransactionDateRange } from "../../utils/formatters"
import { formatCurrency } from "@/lib/utils/formatCurrency"
import { Badge } from "@/shared/ui/atoms/Badge"
import { Button } from "@/shared/ui/atoms/Button"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/atoms/AlertDialog"

interface ConfirmDeleteDialogProps {
  isOpen: boolean
  transaction: CollectionTransaction | null
  accountsById?: Map<number, Account>
  categoriesById?: Map<number, Category>
  isDeleting: boolean
  onConfirm: () => void
  onCancel: () => void
}

export const ConfirmDeleteDialog = React.memo(function ConfirmDeleteDialog({
  isOpen,
  transaction,
  accountsById,
  categoriesById,
  isDeleting,
  onConfirm,
  onCancel,
}: ConfirmDeleteDialogProps) {
  if (!transaction) return null

  const account = accountsById?.get(transaction.account)
  const category = transaction.category ? categoriesById?.get(transaction.category) : undefined
  const currency = account?.currency || "HNL"
  const kindLabel = collectionLabels[transaction._type] || "Movimiento"

  const badgeVariant =
    transaction._type === "EXPENSE"
      ? "destructive"
      : transaction._type === "INCOME"
      ? "default"
      : "secondary"

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => { if (!open) onCancel() }}>
      <AlertDialogContent className="sm:max-w-md gap-5 border-destructive/30 shadow-2xl">
        <AlertDialogHeader className="gap-3">
          <div className="flex items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-destructive/15 text-destructive ring-8 ring-destructive/5">
              <AlertTriangle className="size-5" />
            </div>
            <div className="flex flex-col text-left">
              <AlertDialogTitle className="text-base sm:text-lg">
                ¿Eliminar {kindLabel.toLowerCase()}?
              </AlertDialogTitle>
              <p className="text-xs text-muted-foreground">
                Confirma si deseas remover este registro permanentemente.
              </p>
            </div>
          </div>
        </AlertDialogHeader>

        {/* Transaction Summary Card */}
        <div className="flex flex-col gap-2.5 rounded-lg border border-border/70 bg-muted/30 p-3.5 text-xs">
          <div className="flex items-start justify-between gap-2">
            <span className="font-semibold text-foreground text-sm line-clamp-2">
              {transaction.description || "Sin descripción"}
            </span>
            <Badge variant={badgeVariant} className="text-[11px] shrink-0 font-medium capitalize">
              {kindLabel}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border/50 text-muted-foreground">
            <div className="flex items-center gap-1.5 truncate">
              <Calendar className="size-3.5 shrink-0 text-muted-foreground/80" />
              <span className="truncate">
                {formatTransactionDateRange(transaction.date, transaction.end_date)}
              </span>
            </div>

            <div className="flex items-center gap-1.5 truncate justify-end font-mono font-semibold text-foreground">
              <span>{formatCurrency(Number(transaction.amount || 0), currency)}</span>
            </div>

            <div className="flex items-center gap-1.5 truncate">
              <Landmark className="size-3.5 shrink-0 text-muted-foreground/80" />
              <span className="truncate">{account?.name || "Cuenta no especificada"}</span>
            </div>

            {category ? (
              <div className="flex items-center gap-1.5 truncate justify-end">
                <Tag className="size-3.5 shrink-0 text-muted-foreground/80" />
                <span className="truncate">{category.name}</span>
              </div>
            ) : null}
          </div>
        </div>

        <AlertDialogDescription className="text-xs text-muted-foreground">
          Esta acción no se puede deshacer. Los balances de tus cuentas y métricas del periodo se actualizarán automáticamente.
        </AlertDialogDescription>

        <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isDeleting}
            className="w-full sm:w-auto text-xs sm:text-sm h-9"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
            className="w-full sm:w-auto text-xs sm:text-sm h-9 gap-1.5 font-medium shadow-xs"
          >
            {isDeleting ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                <span>Eliminando…</span>
              </>
            ) : (
              <>
                <Trash2 className="size-3.5" />
                <span>Sí, eliminar</span>
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
})

export default ConfirmDeleteDialog

